import React, { forwardRef, useId } from 'react';
import styled from 'styled-components';
import { Indicator, Path, SecondaryPath, PrimaryPath, IndicatorBaseStyle } from './IndicatorBase.styles.js';
import { defaultProps, SVG_BAR_WIDTH, SVG_CIRCLE_RADIUS, SVG_CIRCLE_WHITESPACE, SVG_STROKE_WIDTH } from './constants.js';
import { toCssPrecision } from './util.js';
import { barPath, circlePath } from './paths.js';

/**
 * IndicatorBase component is used internally by Loader and
 * Progress components.
 *
 * The component draws an SVG image with two overlapping paths and defines
 * a clip path for them. The component does not do anything useful without
 * additional styling.
 */
const _IndicatorBase = forwardRef((props, ref) => {
    const { id: propId, primaryProps, secondaryProps, type, ...rest } = props;
    const id = propId ? propId : useId();
    const clipPathId = `${id}-clippath`;
    const svgPath = type === 'bar' ? barPath : circlePath;
    const preserveAspectRatio = type === 'bar' ? 'none meet' : 'xMidYMid meet';
    const viewBoxWidth = toCssPrecision(type === 'bar'
        ? SVG_BAR_WIDTH
        : 2 * (SVG_CIRCLE_RADIUS + SVG_CIRCLE_WHITESPACE));
    const viewBoxHeight = type === 'bar' ? 2 * SVG_STROKE_WIDTH : viewBoxWidth;
    const viewBox = `0 0 ${viewBoxWidth} ${viewBoxHeight}`;
    return (React.createElement("span", { "data-type": type, id: id, role: "progressbar", ...rest, ref: ref },
        React.createElement(Indicator, { preserveAspectRatio: preserveAspectRatio, viewBox: viewBox },
            type === 'circle' && (React.createElement("defs", null,
                React.createElement("clipPath", { id: clipPathId },
                    React.createElement(Path, { d: svgPath })))),
            React.createElement(SecondaryPath, { ...secondaryProps, clipPath: `url(#${clipPathId})`, d: svgPath }),
            React.createElement(PrimaryPath, { ...primaryProps, clipPath: `url(#${clipPathId})`, d: svgPath }))));
});
_IndicatorBase.displayName = '_IndicatorBase';
const IndicatorBase = styled(_IndicatorBase) `
    ${IndicatorBaseStyle}
`;
IndicatorBase.defaultProps = defaultProps;

export { IndicatorBase };
//# sourceMappingURL=IndicatorBase.js.map
